// Ad-click attribution, read side. The marketing site (plaintosee.cc)
// stores an arriving Google Ads click ID in a `pts_ad_click` cookie
// scoped to .plaintosee.cc — see site/plaintosee-unified/public/js/site.js
// for the write side and the privacy rationale (no Google JS anywhere;
// we track the click, never the person). This app also honors a click-ID
// param on its own URL, for ads that deep-link /apply directly.
//
// The resolved value rides the application body so the operator can
// attribute an inquiry to its ad click; feeding conversions back to
// Google (if ever) happens operator-side from stored applications.

export type AdClickKind = "gclid" | "wbraid" | "gbraid";

export interface AdClick {
  kind: AdClickKind;
  id: string;
  landed_at: string | null;
  /** Campaign slug from the ad's Final URL suffix (`?src=yt`), when the
   * campaign sets one. Null for organic-shaped URLs, for campaigns without
   * a suffix, and for every click banked before campaign labelling existed —
   * so treat it as "unknown", never as "not from an ad". */
  src: string | null;
}

const KINDS: AdClickKind[] = ["gclid", "wbraid", "gbraid"];
const ID_RE = /^[A-Za-z0-9_-]{1,200}$/;
// Same shape as site.js (cookie write) and aurora_cloud/ad_click.py (server).
// Three copies with nothing tying them together — change one, change all
// three, or a slug survives the cookie and dies at the server silently.
const SRC_RE = /^[a-z0-9][a-z0-9_-]{0,31}$/;
const COOKIE_NAME = "pts_ad_click";

/** Fold case and surrounding space before matching — `src=YT` typed into the
 * Ads UI must not become an unlabelled click. Mirrors the server's `_src`. */
function normalizeSrc(raw: unknown): string | null {
  if (typeof raw !== "string") return null;
  const candidate = raw.trim().toLowerCase();
  return SRC_RE.test(candidate) ? candidate : null;
}

export function adClickFromSearch(search: string): AdClick | null {
  let params: URLSearchParams;
  try {
    params = new URLSearchParams(search);
  } catch {
    return null;
  }
  const src = normalizeSrc(params.get("src"));
  for (const kind of KINDS) {
    const id = params.get(kind);
    if (!id) continue;
    if (!ID_RE.test(id)) return null;
    // Landing straight on this page IS the landing time.
    return { kind, id, landed_at: new Date().toISOString(), src };
  }
  return null;
}

export function adClickFromCookie(cookies: string): AdClick | null {
  const pair = cookies
    .split(";")
    .map((c) => c.trim())
    .find((c) => c.startsWith(COOKIE_NAME + "="));
  if (!pair) return null;
  try {
    const parsed = JSON.parse(
      decodeURIComponent(pair.slice(COOKIE_NAME.length + 1)),
    ) as {
      kind?: unknown;
      id?: unknown;
      landed_at?: unknown;
      src?: unknown;
    };
    if (
      typeof parsed.kind !== "string" ||
      !KINDS.includes(parsed.kind as AdClickKind) ||
      typeof parsed.id !== "string" ||
      !ID_RE.test(parsed.id)
    ) {
      return null;
    }
    // A bad `src` drops to null rather than voiding the cookie: the click ID
    // is the load-bearing half, and cookies written before labelling existed
    // have no `src` at all.
    const src = normalizeSrc(parsed.src);
    return {
      kind: parsed.kind as AdClickKind,
      id: parsed.id,
      landed_at: typeof parsed.landed_at === "string" ? parsed.landed_at : null,
      src,
    };
  } catch {
    return null;
  }
}

// A click ID on the current URL wins over the cookie: it's by definition
// the most recent click. Null outside a browser (node-env tests, SSR) —
// attribution is a browser-only concern and must never throw.
export function resolveAdClick(): AdClick | null {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return null;
  }
  return (
    adClickFromSearch(window.location.search) ??
    adClickFromCookie(document.cookie)
  );
}
